import Toast from "components/cms/Toast";
import SectionCard from "components/cms/SectionCard";
import {
  AreaInput,
  CardRow,
  FieldRow,
  FileInput,
  GroupBox,
  TextInput,
} from "components/cms/fields";
import useCmsPage from "hooks/useCmsPage";
import {
  addCompanyPage,
  deleteCompanyPage,
  getCompanyPage,
  updateCompanyPage,
} from "api/companypage";
import { emptyCompany } from "utils/defaults";

const FACTS = ["heroFact1", "heroFact2", "heroFact3", "heroFact4"];
const APPROACH = ["approachCard1", "approachCard2", "approachCard3"];
const LEADERS = ["leader1", "leader2", "leader3", "leader4"];
const STATS = ["stat1", "stat2", "stat3", "stat4"];
const EXPERTISE = ["expertiseCard1", "expertiseCard2", "expertiseCard3"];

export default function CompanyPage() {
  const cms = useCmsPage({
    load: getCompanyPage,
    create: addCompanyPage,
    update: updateCompanyPage,
    remove: deleteCompanyPage,
    empty: emptyCompany,
    mapResponse: (res) => res.data?.data,
  });
  const {
    form,
    setField,
    frMap,
    loading,
    toast,
    setToast,
    saveSection,
    savingSection,
    isSectionDirty,
  } = cms;

  const HERO_KEYS = [
    "heroBackground",
    "heroTag",
    "heroTitle1",
    "heroTitle2",
    "heroPrimaryCtaText",
    "heroPrimaryCtaLink",
    "heroSecondaryCtaText",
    "heroSecondaryCtaLink",
    "heroFact1",
    "heroFact2",
    "heroFact3",
    "heroFact4",
    "heroSectionHide",
  ];
  const APPROACH_KEYS = [
    "approachTag",
    "approachTitle",
    "approachCard1",
    "approachCard2",
    "approachCard3",
    "approachHide",
  ];
  const LEADERSHIP_KEYS = [
    "leadershipTitle1",
    "leadershipTitle2",
    "leadershipDescription",
    "leader1",
    "leader2",
    "leader3",
    "leader4",
    "leadershipHide",
  ];
  const STATS_KEYS = ["stat1", "stat2", "stat3", "stat4", "statsHide"];
  const EXPERTISE_KEYS = [
    "expertiseTitle1",
    "expertiseTitle2",
    "expertiseDescription",
    "expertiseImage",
    "expertiseCard1",
    "expertiseCard2",
    "expertiseCard3",
    "expertiseHide",
  ];

  if (loading) {
    return <p className="mt-5 text-gray-600">Loading Company…</p>;
  }

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <SectionCard
        title="Hero"
        hide={form.heroSectionHide}
        hideId="company-heroSectionHide"
        onHideChange={(value) => setField("heroSectionHide", value)}
        onSave={() => saveSection("hero", HERO_KEYS)}
        saving={savingSection === "hero"}
        canSave={isSectionDirty(HERO_KEYS)}
      >
        <FileInput
          label="Background"
          path="heroBackground"
          form={form}
          setField={setField}
          extra="max-w-xl"
        />
        <FieldRow>
          <TextInput label="Tag" path="heroTag" form={form} setField={setField} frMap={frMap} />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="heroTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="heroTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Primary CTA text"
            path="heroPrimaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Primary CTA link"
            path="heroPrimaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Secondary CTA text"
            path="heroSecondaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Secondary CTA link"
            path="heroSecondaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <CardRow>
          {FACTS.map((key, index) => (
            <GroupBox key={key} title={`Fact ${index + 1}`}>
              <TextInput
                label="Label"
                path={`${key}.label`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="Value"
                path={`${key}.value`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Approach"
        hide={form.approachHide}
        hideId="approachHide"
        onHideChange={(value) => setField("approachHide", value)}
        onSave={() => saveSection("approach", APPROACH_KEYS)}
        saving={savingSection === "approach"}
        canSave={isSectionDirty(APPROACH_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="approachTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title"
            path="approachTitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        {APPROACH.map((key, index) => (
          <GroupBox key={key} title={`Card ${index + 1}`}>
            <FieldRow>
              <TextInput
                label="Number"
                path={`${key}.number`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </FieldRow>
            <AreaInput
              label="Description"
              path={`${key}.description`}
              form={form}
              setField={setField}
              frMap={frMap}
            />
          </GroupBox>
        ))}
      </SectionCard>

      <SectionCard
        title="Leadership"
        hide={form.leadershipHide}
        hideId="leadershipHide"
        onHideChange={(value) => setField("leadershipHide", value)}
        onSave={() => saveSection("leadership", LEADERSHIP_KEYS)}
        saving={savingSection === "leadership"}
        canSave={isSectionDirty(LEADERSHIP_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="leadershipTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="leadershipTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Description"
          path="leadershipDescription"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow>
          {LEADERS.map((key, index) => (
            <GroupBox key={key} title={`Leader ${index + 1}`}>
              <FileInput
                label="Photo"
                path={`${key}.image`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Name"
                path={`${key}.name`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Role"
                path={`${key}.role`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Stats"
        hide={form.statsHide}
        hideId="statsHide"
        onHideChange={(value) => setField("statsHide", value)}
        onSave={() => saveSection("stats", STATS_KEYS)}
        saving={savingSection === "stats"}
        canSave={isSectionDirty(STATS_KEYS)}
      >
        <CardRow>
          {STATS.map((key, index) => (
            <GroupBox key={key} title={`Stat ${index + 1}`}>
              <TextInput
                label="Number"
                path={`${key}.number`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Label"
                path={`${key}.label`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Expertise"
        hide={form.expertiseHide}
        hideId="expertiseHide"
        onHideChange={(value) => setField("expertiseHide", value)}
        onSave={() => saveSection("expertise", EXPERTISE_KEYS)}
        saving={savingSection === "expertise"}
        canSave={isSectionDirty(EXPERTISE_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="expertiseTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="expertiseTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Description"
          path="expertiseDescription"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <FileInput
          label="Image"
          path="expertiseImage"
          form={form}
          setField={setField}
          extra="max-w-xl"
        />
        {EXPERTISE.map((key, index) => (
          <GroupBox key={key} title={`Card ${index + 1}`}>
            <FieldRow>
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </FieldRow>
            <AreaInput
              label="Description"
              path={`${key}.description`}
              form={form}
              setField={setField}
              frMap={frMap}
            />
          </GroupBox>
        ))}
      </SectionCard>
    </div>
  );
}
